/**
 * Location extraction utilities
 * Handles various location data formats from entities
 */

type LocationObject = {
  address?: string;
  name?: string;
  city?: string;
  state?: string;
  country?: string;
  [key: string]: unknown;
};

/**
 * Extract a display-ready location string from various formats
 *
 * Handles:
 * - String locations (returned as-is)
 * - Object with `address` property
 * - Object with `{name, city, state, country}` - constructs "Name, City, State"
 *
 * @returns Trimmed string or null if no valid location found
 */
export function extractLocationString(location: unknown): string | null {
  // String - return trimmed
  if (typeof location === 'string' && location.trim()) {
    return location.trim();
  }

  // Object formats
  if (typeof location === 'object' && location !== null) {
    const loc = location as LocationObject;

    // {address: "..."} format
    if (typeof loc.address === 'string' && loc.address.trim()) {
      return loc.address.trim();
    }

    // {name, city, state, country} format
    if (typeof loc.name === 'string' && loc.name.trim()) {
      const parts = [loc.name, loc.city, loc.state].filter(
        (p): p is string => typeof p === 'string' && p.trim().length > 0
      );
      return parts.join(', ') || null;
    }

    // {city, state} without name
    if (typeof loc.city === 'string' && loc.city.trim()) {
      const parts = [loc.city, loc.state].filter(
        (p): p is string => typeof p === 'string' && p.trim().length > 0
      );
      return parts.join(', ') || null;
    }
  }

  return null;
}

/**
 * Extract location from an entity, checking multiple sources
 *
 * Priority:
 * 1. Google Places formattedAddress
 * 2. Entity location field
 */
export function extractEntityLocation(entity: unknown): string | null {
  if (!entity || typeof entity !== 'object') return null;

  const e = entity as Record<string, unknown>;
  const gp = (e.google_places || e.enrichment_data?.google_places) as Record<string, unknown> | undefined;

  // Try Google Places formatted address first
  if (gp?.formattedAddress) {
    const result = extractLocationString(gp.formattedAddress);
    if (result) return result;
  }
  if (gp?.formatted_address) {
    const result = extractLocationString(gp.formatted_address);
    if (result) return result;
  }

  // Fall back to entity location
  const fromLocation = extractLocationString(e.location);
  if (fromLocation) return fromLocation;

  // Fall back to top-level address (EntitySummary from bbox API)
  if (typeof e.address === 'string' && e.address.trim()) {
    return e.address.trim();
  }

  return null;
}

const KM_TO_MI = 0.621371;
const FEET_PER_MI = 5280;

/**
 * Format a distance in kilometers as a human-readable string in miles/feet.
 * Under 0.1 mi shows feet, otherwise shows miles to one decimal.
 */
export function formatDistance(km: number): string {
  const mi = km * KM_TO_MI;
  return mi < 0.1 ? `${Math.round(mi * FEET_PER_MI)} ft` : `${mi.toFixed(1)} mi`;
}
